import { MagnifyingGlass, X } from '@phosphor-icons/react';
import { AnimatePresence, motion } from 'framer-motion';
import type { Ref } from 'react';

import styles from './SearchField.module.css';

export interface SearchFieldProps {
  query: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Accessible name; defaults to the placeholder. */
  label?: string;
  /**
   * Set while the field is the store's full-screen search surface. It also
   * makes the field editable — collapsed it is deliberately read-only, so
   * tapping it opens the surface instead of raising the keyboard mid-morph.
   */
  expanded?: boolean;
  onOpen?: () => void;
  onFocus?: () => void;
  inputRef?: Ref<HTMLInputElement>;
  /** Shared layout id for the store's expand animation. Omit elsewhere. */
  layoutId?: string;
  /** Drops the bottom margin when the field heads a picker, not a page. */
  flush?: boolean;
  autoFocus?: boolean;
}

/**
 * The one search bar in the app.
 *
 * Every search — the storefront's, the numbers browser's — renders this, so
 * they cannot look almost the same. The store additionally passes `layoutId`
 * to morph it into a full-screen surface; nothing else needs that, and the
 * component behaves as a plain field without it.
 */
export function SearchField({
  query,
  onChange,
  placeholder = 'Search',
  label,
  expanded = false,
  onOpen,
  onFocus,
  inputRef,
  layoutId,
  flush = false,
  autoFocus = false,
}: SearchFieldProps) {
  const className = [styles.search, expanded ? styles.searchExpanded : '', flush ? styles.flush : '']
    .filter(Boolean)
    .join(' ');

  return (
    <motion.div
      {...(layoutId ? { layoutId } : {})}
      className={className}
      transition={{ layout: { duration: 0.34, ease: [0.2, 0, 0, 1] } }}
      onPointerDown={
        expanded || !onOpen
          ? undefined
          : (event) => {
              // Keep the collapsed field read-only until its move finishes;
              // opening the mobile keyboard during FLIP measurement makes the
              // viewport resize fight the morph animation.
              event.preventDefault();
              onOpen();
            }
      }
    >
      <MagnifyingGlass size={18} className={styles.searchIcon} />
      <input
        ref={inputRef}
        className={styles.searchInput}
        type="search"
        aria-label={label ?? placeholder}
        autoComplete="off"
        autoFocus={autoFocus}
        readOnly={Boolean(onOpen) && !expanded}
        placeholder={placeholder}
        value={query}
        onFocus={onFocus ?? onOpen}
        onChange={(event) => onChange(event.target.value)}
      />
      <AnimatePresence initial={false}>
        {query ? (
          <motion.button
            key="clear-search"
            type="button"
            className={styles.clearSearch}
            aria-label="Clear search"
            initial={{ opacity: 0, scale: 0.65, x: 7, rotate: -35 }}
            animate={{ opacity: 1, scale: 1, x: 0, rotate: 0 }}
            exit={{ opacity: 0, scale: 0.65, x: 7, rotate: 35 }}
            transition={{ type: 'spring', stiffness: 430, damping: 27 }}
            onClick={(event) => {
              event.stopPropagation();
              onChange('');
            }}
          >
            <X size={15} weight="bold" />
          </motion.button>
        ) : null}
      </AnimatePresence>
    </motion.div>
  );
}
